/**
 * Channel provider metadata — single source of truth driving the provider
 * picker and the per-provider config form. Field names match BE exactly
 * (see internal/notification/factory.go).
 */
import {
  Send,
  Mail,
  Slack,
  Webhook,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ChannelType } from '@/api/healthcheck';

export interface ProviderField {
  name: string;
  label: string;
  /** 'text' | 'number' | 'password' (secret, reveal toggle) */
  type: 'text' | 'number' | 'password';
  required: boolean;
  placeholder?: string;
  helper?: string;
}

export interface ProviderMeta {
  label: string;
  icon: LucideIcon;
  /** One-line summary: what it is / when to use. */
  description: string;
  /** Short ordered steps to obtain credentials. */
  setupSteps: string[];
  /** Official documentation URL (optional). */
  docsUrl?: string;
  /** Fields whose values are encrypted on BE — omit from PUT if unchanged. */
  secretFields: string[];
  fields: ProviderField[];
}

/**
 * Registry keyed by ChannelType.
 * Field names are verbatim from internal/notification/factory.go.
 */
export const PROVIDER_META: Record<ChannelType, ProviderMeta> = {
  telegram: {
    label: 'Telegram',
    icon: Send,
    description: 'Send alerts to a Telegram chat, group, or channel via a bot.',
    setupSteps: [
      'Open @BotFather in Telegram and send /newbot; copy the bot token.',
      'Add the bot to your target group/channel (give it permission to post).',
      'Get the chat ID (e.g. message @userinfobot, or use the bot getUpdates API).',
      'Add each destination as a subscriber with its chat ID as the key.',
    ],
    docsUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
    secretFields: ['bot_token'],
    fields: [
      {
        name: 'bot_token',
        label: 'Bot token',
        type: 'password',
        required: true,
        placeholder: '123456789:ABCDEF...',
        helper: 'Telegram bot token from @BotFather.',
      },
      {
        name: 'chat_id',
        label: 'Default chat ID',
        type: 'text',
        required: false,
        placeholder: '-100123456789',
        helper: 'Optional. Can be set per-subscriber instead.',
      },
    ],
  },

  email: {
    label: 'Email (SMTP)',
    icon: Mail,
    description: 'Send alerts by email through your own SMTP server.',
    setupSteps: [
      'Enter your SMTP host and port (587 = STARTTLS, 465 = SSL).',
      'Set username/password if your server requires auth (Gmail: use an App Password, not your login).',
      'Set the From address recipients will see.',
      'Add each recipient as a subscriber with their email as the key.',
    ],
    secretFields: ['password'],
    fields: [
      {
        name: 'smtp_host',
        label: 'SMTP host',
        type: 'text',
        required: true,
        placeholder: 'smtp.example.com',
      },
      {
        name: 'smtp_port',
        label: 'SMTP port',
        type: 'number',
        required: false,
        placeholder: '587',
        helper: 'Default 587 (STARTTLS).',
      },
      {
        name: 'username',
        label: 'Username',
        type: 'text',
        required: false,
        placeholder: 'alerts@example.com',
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        required: false,
        placeholder: '••••••••',
      },
      {
        name: 'from_address',
        label: 'From address',
        type: 'text',
        required: false,
        placeholder: 'alerts@example.com',
        helper: 'Sender email shown to recipients.',
      },
    ],
  },

  slack: {
    label: 'Slack',
    icon: Slack,
    description: 'Post alerts to a Slack channel via an Incoming Webhook.',
    setupSteps: [
      'In Slack: create/Manage apps → Incoming Webhooks → Activate.',
      'Add New Webhook to Workspace and pick the target channel.',
      'Copy the webhook URL.',
      'Use one webhook for the channel, or set a different webhook per subscriber.',
    ],
    docsUrl: 'https://api.slack.com/messaging/webhooks',
    secretFields: ['webhook_url'],
    fields: [
      {
        name: 'webhook_url',
        label: 'Incoming webhook URL',
        type: 'password',
        required: false,
        placeholder: 'https://hooks.slack.com/services/...',
        helper: 'Optional at channel level — can be set per-subscriber.',
      },
    ],
  },

  teams: {
    label: 'Microsoft Teams',
    icon: Users,
    description: 'Post alerts to a Microsoft Teams channel via an Incoming Webhook.',
    setupSteps: [
      'In the Teams channel: ••• → Connectors (or Workflows) → Incoming Webhook.',
      'Name it and copy the generated webhook URL.',
      'Use one webhook for the channel, or set a different webhook per subscriber.',
    ],
    docsUrl: 'https://learn.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook',
    secretFields: ['webhook_url'],
    fields: [
      {
        name: 'webhook_url',
        label: 'Incoming webhook URL',
        type: 'password',
        required: false,
        placeholder: 'https://xxx.webhook.office.com/...',
      },
    ],
  },

  webhook: {
    label: 'Webhook',
    icon: Webhook,
    description: 'Send a signed JSON POST to your own HTTP endpoint.',
    setupSteps: [
      'Set the endpoint URL that will receive alert POSTs (JSON body).',
      'Optionally set an HMAC secret; we sign each request with X-Webhook-Signature so you can verify authenticity.',
      'Set the URL at channel level, or per subscriber.',
    ],
    secretFields: ['secret'],
    fields: [
      {
        name: 'webhook_url',
        label: 'Endpoint URL',
        type: 'text',
        required: false,
        placeholder: 'https://example.com/webhook',
        helper: 'Optional at channel level — can be set per-subscriber.',
      },
      {
        name: 'secret',
        label: 'HMAC signing secret',
        type: 'password',
        required: false,
        placeholder: '••••••••',
        helper: 'Sent as X-Webhook-Signature header for verification.',
      },
    ],
  },
};

export const PROVIDER_TYPES = Object.keys(PROVIDER_META) as ChannelType[];

export function providerMeta(type: ChannelType): ProviderMeta {
  return PROVIDER_META[type];
}
